import React, { useCallback, useState } from "react";

export type MenuAnchorState = [
	anchor: null | HTMLElement,
	open: (event: React.MouseEvent<HTMLElement>) => void,
	close: () => void,
	passthroughClose: <T extends (...args: unknown[]) => unknown>(fn: T) => T,
];

/**
 * Exposes common state for an anchored menu
 */
export default function useMenuAnchorState(): MenuAnchorState {
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();

			setMenuAnchor(event.currentTarget);
		},
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, [setMenuAnchor]);
	const transformClose = useCallback(
		<T extends (...args: unknown[]) => unknown>(fn: T): T => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			return useCallback(
				((...args: unknown[]) => {
					closeMenu();
					return fn(...args);
				}) as T,
				[closeMenu, fn],
			);
		},
		[closeMenu],
	);

	return [menuAnchor, openMenu, closeMenu, transformClose];
}
